import { useCallback, useEffect, useMemo, useRef } from 'react'

export interface DebouncedCallbacks<A extends unknown[]> {
  (...args: A): void
  flush: () => void
  cancel: () => void
}

/**
 * Debounces invoking `fn` until `ms` after the last call.
 * `flush()` runs any pending invocation immediately.
 * On unmount, pending arguments are flushed once so saves are not dropped.
 */
export function useDebouncedCallback<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number
): DebouncedCallbacks<A> {
  const fnRef = useRef(fn)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingRef = useRef<A | null>(null)
  fnRef.current = fn

  const cancel = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    pendingRef.current = null
  }, [])

  const flush = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    const pending = pendingRef.current
    pendingRef.current = null
    if (pending !== null) fnRef.current(...pending)
  }, [])

  const debounced = useCallback(
    (...args: A) => {
      pendingRef.current = args
      if (timerRef.current != null) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        const p = pendingRef.current
        pendingRef.current = null
        if (p !== null) fnRef.current(...p)
      }, ms)
    },
    [ms]
  )

  useEffect(() => {
    return () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      const p = pendingRef.current
      pendingRef.current = null
      if (p !== null) fnRef.current(...p)
    }
  }, [])

  return useMemo(
    () => Object.assign(debounced, { flush, cancel }) as DebouncedCallbacks<A>,
    [debounced, flush, cancel]
  )
}
